import { ChevronDown, type LucideIcon } from 'lucide-react'
import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { cn } from '../lib/cn'
import { useLink } from './context'
import { DropdownMenu } from './DropdownMenu'

export interface TabItem<V extends string = string> {
  value: V
  label: string
  icon?: LucideIcon
  count?: number
  disabled?: boolean
  /** der aktive Reiter steht in der URL, damit Links und „Zurück“ funktionieren */
  href?: string
}

export interface TabsProps<V extends string = string> {
  value: V
  onChange: (value: V) => void
  items: readonly TabItem<V>[]
  /** 'underline' für die Seitenebene, 'pill' innerhalb von Karten */
  variant?: 'underline' | 'pill'
  size?: 'sm' | 'md'
  /** Standard 'menu': was nicht passt, wandert in „Mehr“ */
  overflow?: 'scroll' | 'menu'
  ariaLabel: string
  className?: string
}

/** Reiter wechseln die Sicht auf denselben Datensatz. Aktiv: Text fg mit 2-px-Linie in fg (nicht in Akzent). */
export function Tabs<V extends string = string>({
  value,
  onChange,
  items,
  variant = 'underline',
  size = 'md',
  overflow = 'menu',
  ariaLabel,
  className,
}: TabsProps<V>) {
  const Link = useLink()
  const listRef = useRef<HTMLDivElement | null>(null)
  const [hidden, setHidden] = useState<readonly string[]>([])

  // Überlauf messen: Die Liste bricht um und ist auf eine Zeile beschnitten – was in Zeile zwei landet, kommt in „Mehr“.
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list || overflow !== 'menu' || typeof ResizeObserver === 'undefined') return
    const measure = (): void => {
      const tabs = [...list.querySelectorAll<HTMLElement>('[role="tab"]')]
      const top = tabs[0]?.offsetTop ?? 0
      const next = tabs.filter((tab) => tab.offsetTop > top + 4).map((tab) => tab.dataset.value as string)
      setHidden((current) => (current.join('|') === next.join('|') ? current : next))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(list)
    return () => observer.disconnect()
  }, [overflow, items])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const visible = items.filter((item) => !item.disabled && !hidden.includes(item.value))
    const index = visible.findIndex((item) => item.value === (event.target as HTMLElement).dataset.value)
    let next: TabItem<V> | undefined
    if (event.key === 'ArrowRight') next = visible[(index + 1) % visible.length]
    else if (event.key === 'ArrowLeft') next = visible[(index - 1 + visible.length) % visible.length]
    else if (event.key === 'Home') next = visible[0]
    else if (event.key === 'End') next = visible[visible.length - 1]
    if (!next) return
    event.preventDefault()
    onChange(next.value)
    listRef.current?.querySelector<HTMLElement>(`[data-value="${CSS.escape(next.value)}"]`)?.focus()
  }

  const underline = variant === 'underline'
  const height = underline
    ? size === 'sm'
      ? 'h-8 touch:h-11'
      : 'h-10 touch:h-12'
    : size === 'sm'
      ? 'h-control-sm'
      : 'h-control'
  const overflowItems = items.filter((item) => hidden.includes(item.value))
  const activeHidden = hidden.includes(value)

  return (
    <div
      className={cn(
        'flex min-w-0 items-stretch',
        underline ? 'border-b border-line' : 'w-fit max-w-full rounded-sm bg-sunken p-0.5',
        className,
      )}
    >
      <div
        ref={listRef}
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        className={cn(
          'flex min-w-0 flex-1',
          underline ? 'gap-5' : 'gap-0.5',
          overflow === 'menu' ? cn('flex-wrap overflow-hidden', height) : 'scrollbar-none overflow-x-auto',
          underline && '-mb-px',
        )}
      >
        {items.map((item) => {
          const active = item.value === value
          const Icon = item.icon
          const isHidden = hidden.includes(item.value)
          const classes = cn(
            'hit-area inline-flex shrink-0 items-center gap-1.5 text-dense font-medium whitespace-nowrap transition-colors duration-(--duration-instant) ease-snap touch:text-touch',
            height,
            underline
              ? cn(
                  'focus-inset border-b-2',
                  active ? 'border-fg text-fg' : 'border-transparent text-fg-muted hover:text-fg',
                )
              : cn(
                  'rounded-xs px-2.5',
                  active ? 'bg-surface text-fg shadow-sm dark:bg-overlay-hover' : 'text-fg-muted hover:text-fg',
                ),
            item.disabled && 'cursor-not-allowed text-fg-disabled hover:text-fg-disabled',
          )
          const content = (
            <>
              {Icon && <Icon className="size-4 shrink-0" strokeWidth={1.75} aria-hidden />}
              {item.label}
              {item.count !== undefined && (
                <span
                  className={cn(
                    'rounded-full px-1.5 text-micro tracking-normal tabular-nums',
                    active ? 'bg-pressed text-fg' : 'bg-sunken text-fg-muted',
                  )}
                >
                  {item.count}
                </span>
              )}
            </>
          )
          const shared = {
            role: 'tab' as const,
            'aria-selected': active,
            'aria-disabled': item.disabled || undefined,
            'aria-hidden': isHidden || undefined,
            tabIndex: active && !isHidden ? 0 : -1,
            'data-value': item.value,
            className: classes,
          }
          if (item.href && !item.disabled) {
            return (
              <Link key={item.value} href={item.href} {...shared} onClick={() => onChange(item.value)}>
                {content}
              </Link>
            )
          }
          return (
            <button key={item.value} type="button" {...shared} onClick={() => !item.disabled && onChange(item.value)}>
              {content}
            </button>
          )
        })}
      </div>
      {overflowItems.length > 0 && (
        <DropdownMenu
          align="end"
          trigger={
            <button
              type="button"
              className={cn(
                'ml-3 inline-flex shrink-0 items-center gap-1 text-dense font-medium',
                height,
                underline && '-mb-px border-b-2',
                activeHidden ? 'border-fg text-fg' : 'border-transparent text-fg-muted hover:text-fg',
              )}
            >
              Mehr
              <ChevronDown className="size-4" strokeWidth={1.75} aria-hidden />
            </button>
          }
          items={overflowItems.map((item) => ({
            type: 'radio' as const,
            id: item.value,
            label: item.label,
            checked: item.value === value,
            disabled: item.disabled,
            meta: item.count,
            onSelect: () => onChange(item.value),
          }))}
        />
      )}
    </div>
  )
}
